import React, { useState } from "react";
import { Container, Form, Row, Col, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { submitForm } from "../../Helpers/API/formsAPI";
import { getRegionConfig } from "./config/regionConfig";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";

function MayaAccountDetails({ navigation, primaryDetails, setPrimaryDetails }) {
  const navigate = useNavigate();
  const [isError, setIsError] = useState({
    maya_account_holder_name: false,
    maya_account_number: false,
    maya_account_photo: false,
    is_incentive_confirmed: false,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        maya_account_photo_temp_path: uploadResult.temp_path
      }));
      if (isError.maya_account_photo) {
        setIsError(prev => ({ ...prev, maya_account_photo: false }));
      }
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return;
    setPrimaryDetails(prev => ({ ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError({
        ...isError,
        [name]: false,
      });
    }
  };

  const validateForm = () => {
    const isMerienda = primaryDetails.is_incentive_confirmed === "merienda";

    const errors = {
      maya_account_holder_name: !isMerienda && !primaryDetails.maya_account_holder_name,
      maya_account_number: !isMerienda && !primaryDetails.maya_account_number,
      maya_account_photo: !isMerienda && !primaryDetails.maya_account_photo_temp_path,
      is_incentive_confirmed: !primaryDetails.is_incentive_confirmed,
    };

    setIsError(errors);

    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    const isMerienda = primaryDetails.is_incentive_confirmed === "merienda";

    return !!primaryDetails.is_incentive_confirmed &&
      (isMerienda || (
        !!primaryDetails.maya_account_holder_name &&
        !!primaryDetails.maya_account_number &&
        !!primaryDetails.maya_account_photo_temp_path
      ));
  };
  const handleNext = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

   const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const regionConfig = getRegionConfig(primaryDetails?.region);
      const res = await submitForm(primaryDetails, regionConfig);

      if (res?.data) {
        try {
          localStorage.removeItem('maya-form-data');
          localStorage.removeItem('maya-form-details');
          localStorage.removeItem('maya-form-current-step');
          localStorage.removeItem('maya-form-history');
          localStorage.setItem('form_completed', 'true');
        } catch (e) {}
        setShowConfirmModal(false);
        navigate('/timein', { replace: true });
      } else {
        toast.error("Submit failed. Please try again.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
      }
    } catch (err) {
      toast.error("Something went wrong while submitting.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  const handleBack = () => {
    navigation.previous();
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">MAYA ACCOUNT DETAILS</h4>
              <p className="form-desc">Description (optional)</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>

                   {/* Confirm Incentive */}
                  <Form.Group className="mb-4" controlId="is_incentive_confirmed">
                    <Form.Label className="form-label">
                      Confirm Incentive <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="is_incentive_confirmed"
                      value={primaryDetails.is_incentive_confirmed || ""}
                      onChange={handleInputChange}
                      className={`form-input ${isError.is_incentive_confirmed ? "error" : ""}`}
                    >
                      <option value="">Select an incentive</option>
                      <option value="maya_credits">Maya Credits</option>
                      <option value="merienda">Merienda</option>
                    </Form.Select>
                    {isError.is_incentive_confirmed && (
                      <div className="form-error">Please select an incentive.</div>
                    )}
                  </Form.Group>

                  {/* Maya Fields — hidden if Merienda is selected */}
                  {primaryDetails.is_incentive_confirmed !== "merienda" && (
                    <>
                      {/* Verified Maya Account Photo */}
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Upload Verified Maya Account Photo <span className="form-required">*</span>
                          {primaryDetails.maya_account_photo_temp_path && !isUploading && (
                            <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          isInvalid={isError.maya_account_photo}
                          disabled={isUploading}
                        />
                        {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                        {isError.maya_account_photo && (
                          <div className="form-error">Required</div>
                        )}
                      </Form.Group>

                      {/* Maya Account Name */}
                      <Form.Group className="mb-4" controlId="maya_account_holder_name">
                        <Form.Label className="form-label">
                          MAYA Account Name <span className="form-required">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="maya_account_holder_name"
                          value={primaryDetails.maya_account_holder_name || ""}
                          onChange={handleInputChange}
                          placeholder="Short answer text"
                          className={`form-input ${isError.maya_account_holder_name ? "error" : ""}`}
                        />
                        {isError.maya_account_holder_name && (
                          <div className="form-error">Please enter Maya Account Name.</div>
                        )}
                      </Form.Group>

                      {/* Verified Maya Account No. */}
                      <Form.Group className="mb-4" controlId="maya_account_number">
                        <Form.Label className="form-label">
                          MAYA Account No. <span className="form-required">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="maya_account_number"
                          value={primaryDetails.maya_account_number || ""}
                          onChange={handleInputChange}
                          placeholder="Short answer text"
                          className={`form-input ${isError.maya_account_number ? "error" : ""}`}
                        />
                        {isError.maya_account_number && (
                          <div className="form-error">Please enter Verified Maya Account No.</div>
                        )}
                      </Form.Group>
                    </>
                  )}

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Submit
                    </AppButton>
                  </div>

                </Form>
              </div>
            </div>
          </Col>
        </Row>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={handleCancelSubmit} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold text-primary">
              ⚠️ Confirm Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
            </div>
            <h5 className="mb-3 text-dark">Are you sure you want to proceed?</h5>
            <p className="text-muted mb-0">
              <strong>Once submitted, you can't go back to make changes.</strong>
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={handleCancelSubmit}
              className="px-4 me-3"
              style={{minWidth: '100px'}}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleConfirmSubmit}
              className="px-4"
              style={{minWidth: '100px', backgroundColor: '#007bff'}}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Submitting...
                </>
              ) : (
                'Yes, Proceed'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default MayaAccountDetails;
